import { useCallback, useMemo, useState } from "react";

import { FormType, getField, Locales } from "@simplyk/common";
import { UseFormReturn, useWatch } from "react-hook-form";

import { trpc } from "@/common/helpers/trpc";
import { EditFundData } from "@/components/FundModals/EditFundModal";
import { AutocompleteOption } from "@/dashboard-design-system/Autocomplete/types";
import type { StepAdvancedSettingsFormData } from "@/features/LiveFormEditor/Steps/StepAdvancedSettings/types";

interface UseFundDesignationArgs {
  form: UseFormReturn<StepAdvancedSettingsFormData>;
  formId: string;
  formType: FormType;
  locale: Locales;
}

export const useFundDesignation = ({ form, formId, formType, locale }: UseFundDesignationArgs) => {
  const utils = trpc.useUtils();
  const { control } = form;

  const [isCreateFundOpen, setIsCreateFundOpen] = useState(false);
  const [editFundData, setEditFundData] = useState<EditFundData | null>(null);

  const isEnabled = useWatch({ control, name: "fundDesignationEnabled" });
  const selectedFundId = useWatch({ control, name: "selectedFundId" });

  const { data: formFundsResult, isLoading: isLoadingFormFunds } = trpc.getFormFunds.useQuery(
    { formId, formType, locale },
    { enabled: Boolean(formId) }
  );

  const { data: organizationFunds } = trpc.getFundsByOrganizationId.useQuery(undefined, {
    enabled: isEnabled,
  });

  const linkedFundCount = formFundsResult?.funds.length ?? 0;
  const isHidden = isLoadingFormFunds || linkedFundCount > 1;

  const fundOptions: AutocompleteOption<string>[] = useMemo(
    () =>
      (organizationFunds ?? []).map((fund) => ({
        label: getField(fund.fundFields, locale)?.name ?? fund.id,
        value: fund.id,
      })),
    [organizationFunds, locale]
  );

  const selectedOption = useMemo(
    () => fundOptions.find((opt) => opt.value === selectedFundId) ?? null,
    [fundOptions, selectedFundId]
  );

  const handleToggle = useCallback(
    (checked: boolean) => {
      if (checked) {
        const currentFundId = form.getValues("selectedFundId");
        if (!currentFundId && organizationFunds?.[0]?.id) {
          form.setValue("selectedFundId", organizationFunds[0].id, { shouldDirty: true });
        }
      } else {
        form.setValue("selectedFundId", null, { shouldDirty: true });
      }
    },
    [form, organizationFunds]
  );

  const handleFundChange = useCallback(
    (newValue: unknown) => {
      const option = newValue as AutocompleteOption<string> | null;
      form.setValue("selectedFundId", option?.value ?? null, { shouldDirty: true });
    },
    [form]
  );

  const handleFundCreated = useCallback(
    (fundId: string) => {
      form.setValue("selectedFundId", fundId, { shouldDirty: true });
      utils.getFundsByOrganizationId.invalidate();
    },
    [form, utils]
  );

  const handleEditFund = useCallback((event: React.MouseEvent, fund: NonNullable<typeof organizationFunds>[number]) => {
    event.stopPropagation();
    event.preventDefault();
    setEditFundData({ id: fund.id, code: fund.code ?? null, fundFields: fund.fundFields });
  }, []);

  return {
    isHidden,
    isEnabled,
    control,
    fundOptions,
    selectedOption,
    organizationFunds,
    isCreateFundOpen,
    editFundData,
    handleToggle,
    handleFundChange,
    handleFundCreated,
    handleEditFund,
    openCreateFund: () => setIsCreateFundOpen(true),
    closeCreateFund: () => setIsCreateFundOpen(false),
    closeEditFund: () => setEditFundData(null),
  };
};
